// Single-use secrets that arrive in links (email verification and password
// reset codes, gift card codes, redemption confirmation codes, account setup
// links). Third-party scripts record the page URL (Branch sends
// current_url, GA page_location, the Facebook Pixel and Clarity the page
// address), so these parameters are taken out of the address bar before any
// of them starts, and kept in memory for the page that needs them.
const SENSITIVE_PARAMS_BY_PATH = {
  '/verify-email': ['code'],
  '/password-reset': ['code'],
  '/redeem': ['code'],
  '/gift-card-redemption-confirmation': ['code'],
  '/gift-card-account-setup': ['code'],
};

// Marks the history entry the page was opened with. The stashed values
// belong to that entry only, as if they were still in its address, so a
// later navigation (even to the same path) no longer sees them.
const STASH_MARKER = 'sensitiveUrlParamsStashed';

let stashedParams = {};

function isStashedHistoryEntry() {
  return !!window.history?.state?.[STASH_MARKER];
}

function normalizePath(pathname) {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * Remove sensitive query parameters from the current URL.
 * Must run before analytics and attribution SDKs are initialised.
 */
export function stashSensitiveUrlParams() {
  if (typeof window === 'undefined' || !window.location) {
    return;
  }
  const { pathname, search, hash } = window.location;
  const path = normalizePath(pathname);
  const names = SENSITIVE_PARAMS_BY_PATH[path];
  if (!names || !search) {
    return;
  }
  const params = new URLSearchParams(search);
  const found = {};
  names.forEach((name) => {
    if (params.has(name)) {
      found[name] = params.get(name);
      params.delete(name);
    }
  });
  if (!Object.keys(found).length) {
    return;
  }
  stashedParams = { path, params: found };
  const query = params.toString();
  const state = window.history.state;
  const baseState = state && typeof state === 'object' ? state : {};
  window.history.replaceState(
    { ...baseState, [STASH_MARKER]: true },
    '',
    `${pathname}${query ? `?${query}` : ''}${hash}`,
  );
}

/**
 * Read a sensitive parameter of the current page: from the URL if it is
 * still there, otherwise from what was stashed at page load (only while the
 * history entry the page was opened with is current).
 */
export function getSensitiveUrlParam(name) {
  const fromUrl = new URLSearchParams(window?.location?.search).get(name);
  if (fromUrl) {
    return fromUrl;
  }
  if (
    !isStashedHistoryEntry() ||
    stashedParams.path !== normalizePath(window?.location?.pathname ?? '')
  ) {
    return null;
  }
  return stashedParams.params?.[name] ?? null;
}

stashSensitiveUrlParams();
